import {
  type EcomFooterProps,
  PdpHeader,
  useFooterContext,
} from '@jouzen/ecom-components/layout';
import { RingFinishEnum } from '@jouzen/ecom-components/v3/enums/ringFinish';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import {
  ProductSelect,
  type ProductSelectProps,
} from '@jouzen/ecom-components/v3/ProductSelect';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useInView } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { type ReactNode, useRef } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import PageLayout, {
  type HeaderProps,
  type PageLayoutProps,
} from '@/app/components/PageLayout';
import { useCartContext } from '@/app/contexts/CartProvider';
import { useDecagonContext } from '@/app/contexts/DecagonProvider';
import useIsScrolledToBottomOfRef from '@/app/hooks/useIsScrolledToBottomOfRef';
import type { ListProduct } from '@/queries/Products';

import type { UseRingScroller } from '../_hooks/types';
import useIsCarouselScrolledToBottom from '../_hooks/useIsCarouselScrolledToBottom';
import type { BuildHardwareOrderBaseProps } from '../_types/order';
import FinishSelections, {
  type FinishSelectionsProps,
} from '../FinishSelections';
import FloatingBottomBar from '../FloatingBottomBar';
import HeroCarousel, { type HeroCarouselProps } from '../HeroCarousel';
import OrderSummary from '../OrderSummary';
import HsaFsaEligible from './HsaFsaEligible';
import MaxQuantityReachedMessage from './MaxQuantityReachedMessage';

export interface BuildHardwareOrderProps extends BuildHardwareOrderBaseProps {
  readonly carousel: HeroCarouselProps;
  readonly children?: ReactNode;
  readonly containerProps: PageLayoutProps['containerProps'];
  readonly finishSelectionsProps: FinishSelectionsProps;
  readonly footerProps: Partial<EcomFooterProps>;
  readonly hasFooterMultivariatePayments?: boolean;
  readonly headerProps?: Partial<HeaderProps>;
  readonly isAddingToCart: boolean;
  readonly isHsaFsaEligible?: boolean;
  readonly onAddToCart: () => Promise<void>;
  readonly or4ChargerPrice?: ListProduct['price'] | undefined;
  readonly pageName: string;
  readonly preFooterContentProps?: PageLayoutProps['preFooterContentProps'];
  readonly productInfoBarTitle?: MessageKey;
  readonly productSelectData: ProductSelectProps[];
  readonly steloPrice?: ListProduct['price'] | undefined;
  readonly title: MessageKey;
}

const OR_4_RING_IMAGES: Partial<Record<RingFinishEnum, string>> = {
  [RingFinishEnum.Black]: 'hardware-pdp/hero-carousel/or4-black-angle-view.png',
  [RingFinishEnum.BrushedSilver]:
    'hardware-pdp/hero-carousel/or4-brushed-silver-angle-view.png',
  [RingFinishEnum.Gold]: 'hardware-pdp/hero-carousel/or4-gold-angle-view.png',
  [RingFinishEnum.RoseGold]:
    'hardware-pdp/hero-carousel/or4-rose-gold-angle-view.png',
  [RingFinishEnum.Silver]:
    'hardware-pdp/hero-carousel/or4-silver-angle-view.png',
  [RingFinishEnum.Stealth]:
    'hardware-pdp/hero-carousel/or4-stealth-angle-view.png',
  [RingFinishEnum.Midnight]:
    'jade-pop/midnight-angle-view.png?trim=alpha&trim-alpha=0',
  [RingFinishEnum.Cloud]:
    'jade-pop/cloud-angle-view.png?trim=alpha&trim-alpha=0',
  [RingFinishEnum.Tide]:
    'membership/ceramic_tide_tilt.png?trim=alpha&trim-alpha=0',
  [RingFinishEnum.Petal]:
    'jade-pop/petal-angle-view.png?trim=alpha&trim-alpha=0',
};

const BuildHardwareOrder = ({
  carousel,
  children,
  containerProps,
  finishSelectionsProps,
  focusWithinProps,
  footerProps,
  hasFooterMultivariatePayments = false,
  headerProps,
  isAddingToCart,
  isHsaFsaEligible = false,
  order,
  or4ChargerPrice,
  pageName,
  preFooterContentProps,
  product,
  productInfoBarTitle,
  productSelectData,
  sectionRefs,
  steloPrice,
  title,
  ...rest
}: BuildHardwareOrderProps & UseRingScroller) => {
  const t = useTranslations();
  const { refreshCart, maxRingQuantityReached } = useCartContext();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const isCarouselScrolledToBottom = useIsCarouselScrolledToBottom(carouselRef);
  const finishSelectionsRef = useRef<HTMLDivElement>(null);
  const isScrolledToBottomOfFinishSelections =
    useIsScrolledToBottomOfRef(finishSelectionsRef);
  const { isChatbotEnabled } = useDecagonContext();
  const { isFooterInView } = useFooterContext();
  const isBuildHardwareOrderInView = useInView(wrapperRef);
  const isProductInfoBarVisible =
    !isBuildHardwareOrderInView && !isFooterInView;

  const { currentRoute } = finishSelectionsProps;
  const currentRingCount = refreshCart?.cart?.ringCount ?? 0;

  const image = {
    alt: product?.lineItemImage?.alt ?? '',
    src: OR_4_RING_IMAGES[
      currentRoute.finish.value as keyof typeof OR_4_RING_IMAGES
    ],
  };

  return (
    <PageLayout
      containerProps={containerProps}
      enableScrollAnimation
      footerProps={footerProps}
      footerMultivariatePaymentsProps={{ className: 'py-8' }}
      hasFooterMultivariatePayments={hasFooterMultivariatePayments}
      headerProps={{
        ...headerProps,
        headerPosition: 'fixed',
        isCarouselInView: !isCarouselScrolledToBottom,
      }}
      // TODO: GROW-2738 - PdpHeader no longer needs to be passed in after Jade. We can rely on the 'enableScrollAnimation' and 'isCarouselInView' props. - JR
      HeaderComponent={PdpHeader}
      isCarouselInView={!isCarouselScrolledToBottom}
      pageName={pageName}
      preFooterContentProps={preFooterContentProps}
    >
      <FloatingBottomBar
        canDisplayProductInfoBar={!maxRingQuantityReached && order.ready}
        isProductInfoBarVisible={isProductInfoBarVisible}
        canDisplayChatbot={isChatbotEnabled}
        hideChatbotTriggerButton={!isScrolledToBottomOfFinishSelections}
        productInfoBarProps={{
          isAddingToCart,
          order,
          product,
          productVariant: product?.colorMsgKey as MessageKey,
          ringCount: currentRingCount,
          thumbnailImage: image,
          title: productInfoBarTitle ?? title,
          ...rest,
        }}
      />
      <div ref={wrapperRef}>
        <LayoutGrid rowGap={0}>
          <GridItem
            colEnd={{ sm: 'full', lg: 15, xl: 17 }}
            colStart={{ sm: 'full' }}
            rowStart={{ sm: 1 }}
          >
            <div className="size-full">
              <div
                className="sticky top-0 w-full flex-1 lg:top-24"
                ref={carouselRef}
              >
                <HeroCarousel {...carousel} />
              </div>
            </div>
          </GridItem>
          <GridItem
            colEnd={{ sm: 'full' }}
            colStart={{ sm: 'full' }}
            rowStart={{ sm: 1 }}
          >
            <LayoutGrid className="max-lg:relative max-lg:z-10 max-lg:mt-[calc(70svh_-_2rem)] max-lg:rounded-t-4xl max-lg:bg-sandstone-200 max-lg:pt-6 lg:min-h-full">
              <GridItem
                colEnd={{ sm: 'main' }}
                colStart={{ sm: 'main', lg: 16, xl: 18 }}
              >
                <section className="mt-4 lg:mt-0">
                  <div className="mx-auto w-full max-lg:max-w-sm">
                    <HsaFsaEligible isEligible={isHsaFsaEligible} />
                    <Typography
                      className="mb-1 pt-5 font-medium! tracking-tighter!"
                      data-cy="build-hardware-order-title"
                      Element="h1"
                      height="tight"
                      variant="h4"
                    >
                      {t(title)}
                    </Typography>
                  </div>
                  {!isAddingToCart && maxRingQuantityReached ? (
                    <MaxQuantityReachedMessage
                      currentRingCount={currentRingCount}
                      or4ChargerPrice={or4ChargerPrice}
                      steloPrice={steloPrice}
                    />
                  ) : (
                    order.ready && (
                      <div className="flex flex-col gap-y-16">
                        <div
                          className="relative flex flex-col items-center lg:items-stretch"
                          ref={finishSelectionsRef}
                        >
                          <div
                            className="w-full max-lg:max-w-sm"
                            ref={(element) => {
                              if (element) sectionRefs.current[0] = element;
                            }}
                            {...focusWithinProps}
                          >
                            <FinishSelections {...finishSelectionsProps} />
                          </div>
                        </div>
                        {productSelectData.map((data, index) => {
                          const isLast = index === productSelectData.length - 1;
                          return (
                            <div
                              key={data.title}
                              className="flex flex-col items-center gap-y-16 lg:items-stretch"
                              {...focusWithinProps}
                              ref={(element) => {
                                if (element)
                                  sectionRefs.current[index + 1] = element;
                              }}
                            >
                              <ProductSelect
                                className="w-full max-lg:max-w-sm"
                                {...data}
                              />
                              {isLast && (
                                <div className="w-full">
                                  <OrderSummary
                                    isAddingToCart={isAddingToCart}
                                    product={product}
                                    ringCount={currentRingCount}
                                    order={order}
                                    {...rest}
                                  />
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )
                  )}
                </section>
              </GridItem>
            </LayoutGrid>
          </GridItem>
        </LayoutGrid>
      </div>
      {children}
    </PageLayout>
  );
};

export default BuildHardwareOrder;
